import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { environment } from "@env";
import { OAuthResourceServerErrorHandler } from "angular-oauth2-oidc";
import { EMPTY, Observable, of, throwError } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { ActivityService } from "../services/activity.service";
import { AuthService } from "../services/auth.service";
import { classifyRefreshFailure } from "../services/refresh-token-error";

const applicationContextOrigins = new Set(
  [
    environment.celoAuthApiEndpoint,
    environment.celoApiEndpoint,
    environment.celoBroadcastApiEndpoint,
    environment.celoVideocallApiEndpoint,
  ].map((endpoint) => new URL(endpoint).origin)
);

const resourceServerOrigins = new Set(
  [
    environment.celoApiEndpoint,
    environment.celoBroadcastApiEndpoint,
    environment.celoVideocallApiEndpoint,
  ].map((endpoint) => new URL(endpoint).origin)
);

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  public constructor(
    private authService: AuthService,
    private errorHandler: OAuthResourceServerErrorHandler
  ) {}

  private getRequestOrigin(request: HttpRequest<unknown>): string | null {
    try {
      return new URL(request.url, environment.origin).origin;
    } catch {
      return null;
    }
  }

  private getSessionId() {
    try {
      const activityService = inject(ActivityService);
      return (
        activityService.getSessionId() ??
        activityService.generateAndStoreSessionId()
      );
    } catch {
      return null;
    }
  }

  private getHeadersObservable(
    includeApplicationContext: boolean,
    includeAuthorization: boolean
  ): Observable<Record<string, string>> {
    if (!includeApplicationContext) return of({});

    const headers: Record<string, string> = {
      AppVersion: environment.appVersion,
    };

    const dataRegion = this.authService.getDataRegion();
    if (dataRegion) {
      headers["Celo-Region"] = dataRegion;
    }

    const sessionId = this.getSessionId();
    if (sessionId) {
      headers["celo-session"] = sessionId;
    }

    if (!includeAuthorization) return of(headers);

    return this.authService.getAccessTokenObservable().pipe(
      map((accessToken) => {
        if (accessToken) {
          headers.Authorization = `Bearer ${accessToken}`;
        }
        return headers;
      })
    );
  }

  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const requestOrigin = this.getRequestOrigin(request);
    const includeApplicationContext =
      requestOrigin !== null && applicationContextOrigins.has(requestOrigin);
    const isResourceServerRequest =
      requestOrigin !== null && resourceServerOrigins.has(requestOrigin);

    return this.getHeadersObservable(
      includeApplicationContext,
      isResourceServerRequest
    ).pipe(
      switchMap((headers) => {
        const newRequest = request.clone({
          setHeaders: headers,
          withCredentials: includeApplicationContext,
        });
        return next.handle(newRequest).pipe(
          catchError((err) => {
            if (err.status !== 401 || !isResourceServerRequest) {
              return this.errorHandler.handleError(err);
            }

            return this.authService.refreshToken().pipe(
              catchError((refreshError: unknown) => {
                // A rejected grant means the user is being signed out already,
                // so stay quiet. Any other refresh failure leaves the session
                // intact, so report the original 401 rather than completing
                // silently and letting callers believe the request succeeded.
                if (classifyRefreshFailure(refreshError) === "session-ended") {
                  return EMPTY;
                }
                return throwError(() => err);
              }),
              switchMap(() => {
                return this.getHeadersObservable(true, true);
              }),
              switchMap((headers) => {
                const requestWithRenewedToken = request.clone({
                  setHeaders: headers,
                  withCredentials: true,
                });
                return next.handle(requestWithRenewedToken);
              })
            );
          })
        );
      })
    );
  }
}
